import {
    Text6,
    IconArrowsLeftAndRightRegular,
    Stack,
    Row,
    IconCancelRegular,
    IconInternetDeviceRegular,
    IconPinRegular,
    Text3,
    IconChevronLeftRegular,
    Text2,
    RowList,
    NavigationBar,
    IconWarningRegular,
    skinVars,
    useDialog,
    Text5,
    IconEsimRegular,
    IconLockOpenRegular,
    IconChipSimCardRegular,
    Tag,
    IconMultisimRegular,
    ThemeVariant,
    IconInformationUserRegular,
} from '@telefonica/mistica';
import React, { ReactElement, useEffect, useRef } from 'react';
import './index.scss';
import { useTranslation } from 'react-i18next';
import '../../translations/i18n';
import { useNavigate, useLocation } from 'react-router-dom';
import SkeletonMobile from '../../components/SkeletonMobile';
import { getPukInfo } from '../../services/ProductService';
import { formatMobileNumber } from '../../utils/dataTransformation/unitsConversion';
import { getRoleLine } from '../../utils/dataTransformation/productConsumption';
import { AxiosResponse } from 'axios';
import { setInfoError } from '../../utils/dataTransformation/errorMesssages';
import { TrackingEvents } from '../../utils/dataTransformation/tracking';
import { isWebviewAndroid, isWebviewIOS } from '../../utils/navigation/controlDevices';
import { managementEnvironment } from '../../utils/dataTransformation/environments';
import Stopper from '../../components/Stopper';
import { getSIMSInfo } from '../../services/ConfigureLineService';
import { useConfigureLineContext } from '../../context/configure-line-context/ConfigureLineContext';
import { useQuery } from '@tanstack/react-query';
import BreadCrumbsWrapper from '../../components/BreadcrumbsWrapper';
import useConfigureLine from './utils/hooks';
import useGlobalEscalation from '../../components/GlobalEscalation/GlobalEscalation';
import { isIOS } from 'react-device-detect';
import { usePersonalDataContext } from '../../context/personal-data-context/PersonalDataContext';
import { useEscalation } from '../../hooks/useEscalation';

type ConfigureLineActions = {
    icon: ReactElement;
    title: string;
    description?: string;
    path?: string;
    index: string;
    disable?: boolean;
    details?: string;
    tag?: ReactElement;
    enable?: boolean;
};

type MultiSim = {
    resourceId: string;
    type: 'E_SIM' | 'PHYSICAL_SIM';
    pending: boolean;
};

const ConfigureLinePage: React.FC = () => {
    const navigate = useNavigate();
    const { isConvergent, lineActive: productId, blocked, setESimInstallFlow } = useConfigureLineContext();
    const { checkRestrictions } = useConfigureLine();
    const location = useLocation();
    const consumptions = JSON.stringify(window.localStorage.getItem('storages'));
    const { trackingScreenView } = new TrackingEvents();
    const homeDisabled = managementEnvironment().VITE_STOPPER_HOME_DISABLED;
    const { t } = useTranslation();
    const { dialog } = useDialog();
    const { personalData } = usePersonalDataContext();
    let numberPUK = '';
    const storageJson = localStorage.getItem('storage');
    let storage: any;
    const duplicateEsimEnabled = managementEnvironment().VITE_WEBVIEW_DUPLICATE_ESIM_ENABLED;
    const multisimEnabled = managementEnvironment().VITE_WEBVIEW_MULTISIM_ENABLED;
    const { data: infoSIM } = useQuery({
        queryKey: ['sims-info', productId],
        queryFn: () => getSIMSInfo(productId),
        enabled: !!productId,
        staleTime: 0,
        gcTime: 0,
    });
    const { checkEscalation } = useGlobalEscalation();
    const { escalationStorageListener } = useEscalation();

    useEffect(() => {
        checkRestrictions(productId);
    }, []);

    useEffect(() => {
        if (storageJson && storageJson !== 'undefined') storage = JSON.parse(storageJson);
    }, [storageJson]);

    const pendingMultiEsim = (infoSIM?.multiSims as MultiSim[] | undefined)?.find(
        (sim) => sim.pending && sim.type === 'E_SIM',
    );
    const pendingMultiPhysicalSim = (infoSIM?.multiSims as MultiSim[] | undefined)?.find(
        (sim) => sim.pending && sim.type === 'PHYSICAL_SIM',
    );

    const data: ConfigureLineActions[] = [
        {
            icon: <IconEsimRegular color={skinVars.colors.brand} size={24} />,
            title: t('activateSim'),
            path: '/configure-line/sim/activate-sim',
            description: '',
            index: 'installEsim',
            enable: infoSIM?.pending && infoSIM?.type === 'PHYSICAL_SIM',
        },
        {
            icon: <IconMultisimRegular color={skinVars.colors.brand} size={24} />,
            title: t('activeMultiEsim'),
            path: '/configure-line/sim/feedbackSeeQRESIM',
            index: 'installMultiEsim',
            enable: !!pendingMultiEsim && multisimEnabled,
        },
        {
            icon: <IconChipSimCardRegular color={skinVars.colors.brand} size={24} />,
            title: t('activeMultiSim'),
            path: '/configure-line/sim/activate-sim',
            index: 'installMultiSim',
            enable: !!pendingMultiPhysicalSim && multisimEnabled,
        },
        {
            icon: <IconInternetDeviceRegular color={skinVars.colors.brand} size={24} />,
            title: t('roaming'),
            path: `/configure-line/roaming`,
            description: t('roamingDescription'),
            index: 'roaming',
            enable: !blocked,
        },
        {
            icon: <IconCancelRegular color={skinVars.colors.brand} size={24} />,
            title: t('specialsNumber'),
            path: '/configure-line/blocked-number-specials',
            description: t('specialsNumberDescription'),
            index: 'specialsnumber',
            enable: !blocked,
        },
        {
            icon: <IconLockOpenRegular color={skinVars.colors.brand} size={24} />,
            title: !blocked ? t('blockLine') : t('unlockLine'),
            path: '/configure-line/block-line',
            description: !blocked ? t('blockLineDescription1') : t('blockLineDescription2'),
            index: 'blockLine',
            enable: true,
        },
        {
            icon: <IconArrowsLeftAndRightRegular color={skinVars.colors.brand} size={24} />,
            title: t('callDivert'),
            path: '/configure-line/calldivert',
            description: t('callDivertDescription'),
            index: 'calldivert',
            enable: true,
        },
        {
            icon: <IconChipSimCardRegular color={skinVars.colors.brand} size={24} />,
            title: t('duplicateSim'),
            path: '/configure-line/sim/duplicate',
            description: t('duplicateSimDescription'),
            index: 'duplicateSim',
            enable: !infoSIM?.pending && duplicateEsimEnabled,
        },
        {
            icon: <IconEsimRegular color={skinVars.colors.brand} size={24} />,
            title: t('eSimQR'),
            path: '/configure-line/sim/feedbackSeeQRESIM',
            description: t('eSimQRDescription'),
            index: 'eSimQR',
            enable: infoSIM?.type === 'E_SIM',
        },
        {
            icon: <IconPinRegular color={skinVars.colors.brand} size={24} />,
            title: t('codepuk'),
            index: 'codepuk',
            enable: true,
        },
        {
            icon: <IconMultisimRegular color={skinVars.colors.brand} size={24} />,
            tag: infoSIM?.multiSims ? (
                infoSIM?.multiSims?.some((sim) => sim.pending) ? (
                    <Tag type='warning'>{t('statusMultiSim')}</Tag>
                ) : (
                    <Tag type='success'>{t('statusTag')}</Tag>
                )
            ) : (
                <div></div>
            ),
            title: infoSIM?.multiSims ? t('multiSim') : t('multiSimRequest'),
            path: infoSIM?.multiSims ? '/configure-line/multiSim' : '/configure-line/multiSim/duplicate',
            index: 'multiSim',
            details: '',
            enable: !infoSIM?.pending && multisimEnabled,
        },
    ];

    const timeout = useRef(0);

    useEffect(() => {
        if (!(isWebviewAndroid || isWebviewIOS)) {
            trackingScreenView();
        }

        window.scrollTo(0, 0);

        () => {
            window.clearTimeout(timeout.current);
        };
    }, []);

    const sendToNative = async (url: string, selectedMultiSim?: MultiSim) => {
        if (isIOS) {
            const newWindow = window.open('', 'newWindow');
            sessionStorage.setItem('newWindow', newWindow?.name || '');
        }

        const selectedMultiSims = selectedMultiSim ? [selectedMultiSim] : infoSIM?.multiSims;

        switch (url) {
            case '/configure-line/sim/feedbackSeeQRESIM':
                checkEscalation(url, 'qr');
                escalationStorageListener(url, {
                    phone: infoSIM?.number,
                    multisim: selectedMultiSims,
                    selectedMultiSim,
                });
                break;
            case '/configure-line/sim/duplicate':
                checkEscalation(url, 'duplicate');
                escalationStorageListener(url);
                break;
            default:
                navigate(url, {
                    state: {
                        ...location.state,
                        phone: infoSIM?.number,
                        multisim: selectedMultiSims,
                        selectedMultiSim,
                        escalation: 'OTP',
                    },
                });
                break;
        }
    };

    const showInstallSimsNotAvailableDialog = () => {
        dialog({
            className: 'action-noAvailable-installSims',
            asset: <IconInformationUserRegular color={skinVars.colors.backgroundBrandSecondary} size={40} />,
            title: t('actionNoAvailable'),
            acceptText: t('buttonUnderstoodFeedback'),
            message: t('stopperActivatedSimsPhysicalDescription'),
            onAccept() {},
        });
    };

    const showNoAvailableCifDialog = () => {
        dialog({
            className: 'action-noAvailable-cif',
            asset: <IconInformationUserRegular color={skinVars.colors.backgroundBrandSecondary} size={40} />,
            title: t('actionNoAvailable'),
            acceptText: t('buttonUnderstoodFeedback'),
            message: t('NoAvailableCIFDaon'),
            onAccept() {},
        });
    };

    const hasPendingPhysicalMultiSim = () => {
        const multiSims = infoSIM?.multiSims ?? [];
        return multiSims.some((sim) => sim.type === 'PHYSICAL_SIM' && sim.pending);
    };

    const canOpenInstallFromInstallEsim = () => {
        const multiSims = infoSIM?.multiSims ?? [];
        return multiSims.length === 1 && multiSims[0]?.type === 'PHYSICAL_SIM' && multiSims[0]?.pending;
    };

    const getActionPathById = (actionId: string) => data.find((item) => item.index === actionId)?.path;

    const handleActions = (id: string) => {
        if (id === 'codepuk') {
            getPukInfo(productId)
                .then((results: AxiosResponse) => {
                    if (results?.data) {
                        numberPUK = results.data;
                        dialog({
                            className: 'codePukAlert',
                            asset: <IconPinRegular color={skinVars.colors.brand} size={64} />,
                            title: t('codepuk'),
                            message: (() => {
                                return (
                                    <Stack space={16}>
                                        <Text3 regular color={skinVars.colors.textSecondary}>
                                            {t('codepukDescription')}
                                        </Text3>
                                        <Text5 color={skinVars.colors.textSecondary}>{numberPUK}</Text5>
                                    </Stack>
                                );
                            })() as any,
                            onAccept() {},
                            acceptText: t('buttonUnderstoodFeedback'),
                        });
                    }
                })
                .catch((err) => {
                    const { title, localizedDescription, id } = setInfoError(err);
                    dialog({
                        className: 'codePukAlert',
                        asset: <IconWarningRegular color={skinVars.colors.feedbackErrorBackground} size={53} />,
                        title,
                        message: (() => {
                            return (
                                <Stack space={16}>
                                    <Text3 light color={skinVars.colors.textSecondary}>
                                        {localizedDescription}
                                    </Text3>
                                    {id && (
                                        <Text3 light color={skinVars.colors.textSecondary}>
                                            {`${t('codeError')} ${id}`}
                                        </Text3>
                                    )}
                                </Stack>
                            );
                        })() as any,
                        onAccept() {},
                        acceptText: t('buttonUnderstoodFeedback'),
                    });
                    console.error(err);
                });
        } else if (id === 'blockLine' && !blocked && infoSIM?.multiSims && infoSIM?.multiSims.length > 0) {
            dialog({
                className: 'block-line-multisim',
                asset: <IconWarningRegular color={skinVars.colors.backgroundBrandSecondary} size={40} />,
                title: t('verification_important_information'),
                acceptText: t('buttonUnderstoodFeedback'),
                message: t('blockMultiSimDialogMessage'),
                onAccept() {},
            });
        } else if (id === 'duplicateSim') {
            if (personalData?.fiscalIdType === 'CIF_' || personalData?.fiscalIdType === 'CIF') {
                showNoAvailableCifDialog();
            } else if (infoSIM?.multiSims && infoSIM?.multiSims.length > 0) {
                dialog({
                    className: 'duplicate-sim',
                    asset: <IconWarningRegular color={skinVars.colors.backgroundBrandSecondary} size={40} />,
                    title: t('verification_important_information'),
                    acceptText: t('buttonUnderstoodFeedback'),
                    message: t('dialogTextEsim'),
                    onAccept() {},
                });
            } else {
                const path = getActionPathById(id);
                if (path) {
                    sendToNative(path);
                }
            }
        } else if (id === 'installMultiEsim') {
            if (personalData?.fiscalIdType === 'CIF_' || personalData?.fiscalIdType === 'CIF') {
                showNoAvailableCifDialog();
            } else {
                const multiSims = infoSIM?.multiSims ?? [];
                const selectedEsim = multiSims.find((sim) => sim.pending && sim.type === 'E_SIM') as
                    | MultiSim
                    | undefined;

                setESimInstallFlow('install');
                const path = getActionPathById(id);
                if (path) {
                    sendToNative(path, selectedEsim);
                }
            }
        } else if (id === 'installMultiSim') {
            const multiSims = infoSIM?.multiSims ?? [];

            if (multiSims.length > 1) {
                showInstallSimsNotAvailableDialog();
            } else {
                const selectedPhysical = multiSims.find((sim) => sim.pending && sim.type === 'PHYSICAL_SIM') as
                    | MultiSim
                    | undefined;

                setESimInstallFlow('install');
                const path = getActionPathById(id);
                if (path) {
                    sendToNative(path, selectedPhysical);
                }
            }
        } else if (id === 'eSimQR') {
            if (personalData?.fiscalIdType === 'CIF_' || personalData?.fiscalIdType === 'CIF') {
                showNoAvailableCifDialog();
            } else {
                setESimInstallFlow('qr');
                const path = getActionPathById(id);
                if (path) {
                    sendToNative(path);
                }
            }
        } else if (id === 'installEsim') {
            if (infoSIM?.type === 'PHYSICAL_SIM' && infoSIM?.pending) {
                showInstallSimsNotAvailableDialog();
            } else if (canOpenInstallFromInstallEsim()) {
                const path = getActionPathById(id);
                if (path) {
                    sendToNative(path);
                }
            } else if ((infoSIM?.multiSims?.length ?? 0) >= 2 && hasPendingPhysicalMultiSim()) {
                showInstallSimsNotAvailableDialog();
            }
        } else {
            const path = getActionPathById(id);
            if (path) {
                sendToNative(path);
            }
        }
    };

    const body = () => {
        return (
            <RowList>
                {data.map((card: ConfigureLineActions, index: number) => {
                    if (card.enable) {
                        return (
                            <div className='configureLine-page__body' key={index}>
                                <Row
                                    asset={card.icon}
                                    headline={card.tag}
                                    title={card?.title}
                                    description={card?.description}
                                    onPress={() => handleActions(card.index)}
                                    badge={
                                        card.index === 'installEsim' ||
                                        card.index === 'installMultiSim' ||
                                        card.index === 'installMultiEsim'
                                    }
                                />
                            </div>
                        );
                    }
                })}
            </RowList>
        );
    };

    return homeDisabled ? (
        <Stopper></Stopper>
    ) : (
        <SkeletonMobile
            header={
                <div className='configureLine-page__header'>
                    {!(isWebviewAndroid || isWebviewIOS) && (
                        <>
                            <div className='configureLine-page__header-navigationBar'>
                                <NavigationBar
                                    variant='default'
                                    onBack={() => {
                                        navigate(
                                            !isConvergent ? '/home' : `/mobile?productPsId=${infoSIM?.productPsId}`,
                                        );
                                    }}
                                    title={t('lineManagement')}
                                />
                            </div>
                            <div
                                className='configureLine-page__header-return'
                                onClick={() => {
                                    navigate(!isConvergent ? '/home' : `/mobile?productPsId=${infoSIM?.productPsId}`);
                                }}
                            >
                                <div className='configureLine-page__header-return-icon'>
                                    <IconChevronLeftRegular color={skinVars.colors.textPrimary} size={20} />
                                </div>
                                <Text2 medium color={skinVars.colors.textPrimary}>
                                    {t('returnResponsive')}
                                </Text2>
                            </div>
                        </>
                    )}
                    <div className='configureLine-page__breadcrumbs'>
                        <ThemeVariant>
                            <BreadCrumbsWrapper
                                title={t('lineManagement')}
                                breadcrumbs={
                                    !isConvergent
                                        ? [{ title: t('breadcrumbsHome'), url: '/home' }]
                                        : [
                                              { title: t('breadcrumbsHome'), url: '/home' },
                                              {
                                                  title: getRoleLine(JSON.parse(consumptions), infoSIM?.productPsId!),
                                                  url: `/mobile?productPsId=${infoSIM?.productPsId}`,
                                              },
                                          ]
                                }
                            />
                        </ThemeVariant>
                    </div>
                    <div
                        className='configureLine-page__header-number'
                        style={{ backgroundColor: skinVars.colors.appBarBackground }}
                    >
                        <Text6 color={skinVars.colors.textPrimary}>{formatMobileNumber(infoSIM?.number)}</Text6>
                    </div>
                </div>
            }
            body={body()}
        ></SkeletonMobile>
    );
};

export default ConfigureLinePage;
